import { clsx } from 'clsx';
import { BookOpen, ChartNoAxesColumnIncreasing, Dumbbell, Headphones, Mic, type LucideIcon } from 'lucide-react';
import { useLazyLoadQuery } from 'react-relay';
import { NavLink } from 'react-router-dom';

import { t, type PlainKey } from '../i18n/t';
import { registerFor } from '../profile/register';
import { MeQuery } from './guards';
import { useGuardedGo } from './useGuardedGo';

import type { guardsMeQuery } from '../__generated__/guardsMeQuery.graphql';

/**
 * Five destinations at most, the ceiling a bottom bar can hold at 375 px with
 * labels a learner can read. The account is not one of them: it is somewhere
 * you go to change things, not a place you practise, and it lives behind the
 * picture in `AppHeader`.
 *
 * `/session`, not `/`: `/` only redirects there, so a link to it could never
 * be active.
 */
const TABS: ReadonlyArray<{ to: string; label: PlainKey; icon: LucideIcon; adultOnly?: boolean }> = [
  { to: '/session', label: 'nav-talk', icon: Mic },
  { to: '/history', label: 'nav-progress', icon: ChartNoAxesColumnIncreasing },
  { to: '/practice', label: 'nav-practice', icon: Dumbbell },
  { to: '/books', label: 'nav-books', icon: BookOpen },
  { to: '/lessons', label: 'nav-lessons', icon: Headphones, adultOnly: true },
];

function useTabs() {
  // The same query `AppLayout` already holds, so this is a cache read.
  const me = useLazyLoadQuery<guardsMeQuery>(MeQuery, {});
  const adult = registerFor(me.me?.learner?.defaultCharacter?.audience) === 'adult';
  return TABS.filter((tab) => adult || !tab.adultOnly);
}

/** One destination, the same in the bar and the rail. */
function Tab({
  to,
  label,
  icon: Icon,
  go,
  className,
}: (typeof TABS)[number] & { go: ReturnType<typeof useGuardedGo>; className: string }) {
  return (
    <NavLink
      to={to}
      onClick={go(to)}
      className={({ isActive }) =>
        clsx(
          'group flex min-h-(--hit-min) min-w-0 flex-col items-center justify-center gap-1 rounded-box',
          className,
          isActive ? 'text-primary' : 'text-base-content/60 hover:text-base-content',
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={clsx(
              'flex h-8 w-14 items-center justify-center rounded-full transition-colors',
              isActive ? 'bg-primary/15' : 'group-hover:bg-base-200',
            )}
          >
            <Icon aria-hidden className="size-6" strokeWidth={isActive ? 2.25 : 2} />
          </span>
          <span className={clsx('text-[0.6875rem] leading-tight whitespace-nowrap', isActive && 'font-semibold')}>
            {t(label)}
          </span>
        </>
      )}
    </NavLink>
  );
}

/**
 * A row of the shell on a phone, laid out by hand rather than with daisyUI's
 * `dock`: `dock` is `position: fixed`, an overlay, and inside `AppLayout`'s
 * `h-dvh` column it left this row zero-height with the bar floating over the
 * last 72 px of the scroller, and its `z-index: 1` sat under `SessionView`'s
 * `z-10` bars.
 *
 * `max-w-2xl mx-auto` matches `main`, so a tab sits under the column it opens.
 * From `md` up it gives way to [`NavRail`].
 */
export function BottomNav() {
  const go = useGuardedGo();
  const tabs = useTabs();
  return (
    <nav className="bg-base-100 border-base-300 shrink-0 border-t pb-[env(safe-area-inset-bottom)] md:hidden">
      <div className="mx-auto flex h-18 w-full max-w-2xl items-stretch justify-around px-1">
        {tabs.map((tab) => (
          <Tab key={tab.to} {...tab} go={go} className="flex-1" />
        ))}
      </div>
    </nav>
  );
}

/**
 * The same destinations down the left edge from `md` up, where a bar under a
 * 672 px column left most of a wide window's bottom edge empty and the tabs
 * far from the content's left margin. A column of the shell, beside `main`.
 */
export function NavRail() {
  const go = useGuardedGo();
  const tabs = useTabs();
  return (
    <nav className="border-base-300 hidden w-24 shrink-0 flex-col items-stretch gap-2 border-e px-2 py-4 md:flex">
      {tabs.map((tab) => (
        <Tab key={tab.to} {...tab} go={go} className="py-2" />
      ))}
    </nav>
  );
}
